import {addDays, differenceInCalendarDays, isBefore, startOfDay} from 'date-fns'

import {formatBusinessTimestamp} from 'src/api/business-time'
import type {DispatchYard} from 'src/dispatch/schemas'
import {defaultBillingType, type RequirementBillingType} from 'src/jobs/billing-types'
import {newWorkProjectInput} from 'src/jobs/create-job-submit'
import {isRealJobName, JOB_NAME_HINT, validateResources} from 'src/jobs/create-job-validation'
import {
    parseApiDate,
    parseApiTime,
    toApiDateString,
    toBusinessApiDateString,
} from 'src/jobs/job-schedule-format'
import {jobLocationsToInputs} from 'src/jobs/location-input'
import {billsEachLine, lineAsBilled} from 'src/jobs/order-billing'
import type {JobDetail} from 'src/jobs/schemas'
import {
    type AssetRequirementRow,
    createEmptyAssetRequirementRow,
    createEmptyWorkerRequirementRow,
    createInitialFormValues,
    type CreateJobFormValues,
    type JobLocation,
    type ManualBillableLineItemInput,
    type WorkerRequirementRow,
} from 'src/jobs/types'
import {haversineMiles} from 'src/lib/haversine-miles'
import {isRealSite} from 'src/lib/texas-bounds'
import {validateBillingTerms} from 'src/projects/create-project-validation'
import {toWizardFormValues} from 'src/projects/project-billing-terms-utils'
import type {
    CreateProjectInput,
    CreateProjectJobInput,
    ProjectBillingMethod,
    ProjectBillingTerms,
} from 'src/projects/schemas'
import {quoteToDraft} from 'src/quotes/quote-adapters'
import {
    itemSubtotal,
    lineExtended,
    lineMultiplier,
    quoteBillingMethod,
    type QuoteItemLine,
    quoteLinesToBillingTerms,
    type QuotePercentLine,
    type QuoteRentalPeriod,
    type QuoteUnit,
    rentalPeriodDays,
} from 'src/quotes/quote-builder-logic'
import type {Quote} from 'src/quotes/schemas'

import type {CustomerLastRate} from './schemas'

export type JobEntrySectionId = 'customer' | 'what' | 'crew' | 'billing'

export const JOB_ENTRY_SECTIONS: {id: JobEntrySectionId; title: string}[] = [
    {id: 'customer', title: 'Customer & site'},
    {id: 'what', title: 'What & when'},
    {id: 'crew', title: 'Crew & equipment'},
    {id: 'billing', title: 'Billing'},
]

export type AddToOrder = {
    projectId: string
    orderLabel: string
    customerId: string
    customerName: string
    dispatchYardId: string | null
    sourceJobId: string | null
}

type NamedType = {id: string; name: string}
type WorkerTypeInfo = NamedType & {isTechnician: boolean}
type AssetTypeInfo = NamedType & {isDrivenTruck: boolean}
type CategoryAssetType = NamedType & {
    defaultServiceTypeId: string | null
    defaultSpecificType: string | null
}
type CategoryOption = NamedType & {specificTypes: string[]; isTechnicianWork: boolean}

/**
 * Suggest a category and specific type from job lines: the first asset type with a default
 * category, else the technician category when every worker line is a technician type.
 */
export function suggestCategory(
    lines: {assetTypeIds: (string | null)[]; workerTypeIds: (string | null)[]},
    catalog: {
        assetTypes: CategoryAssetType[]
        workerTypes: WorkerTypeInfo[]
        categories: CategoryOption[]
    },
): {categoryId: string; specificType: string | null} | null {
    const assetTypes = lines.assetTypeIds.flatMap(
        (id) => catalog.assetTypes.find((type) => type.id === id) ?? [],
    )
    for (const assetType of assetTypes) {
        const category = catalog.categories.find(
            (option) => option.id === assetType.defaultServiceTypeId,
        )
        if (category) {
            return {categoryId: category.id, specificType: assetType.defaultSpecificType}
        }
    }
    const workerTypes = lines.workerTypeIds.flatMap(
        (id) => catalog.workerTypes.find((type) => type.id === id) ?? [],
    )
    const onlyTechnicians =
        assetTypes.length === 0 &&
        workerTypes.length > 0 &&
        workerTypes.every((type) => type.isTechnician)
    const technicianWork = onlyTechnicians
        ? catalog.categories.find((option) => option.isTechnicianWork)
        : undefined
    return technicianWork ? {categoryId: technicianWork.id, specificType: null} : null
}

export function nearestYardId(
    yards: DispatchYard[],
    site: Pick<JobLocation, 'latitude' | 'longitude'> | null | undefined,
): string | null {
    if (
        site?.latitude == null ||
        site.longitude == null ||
        !isRealSite(site.latitude, site.longitude)
    ) {
        return null
    }
    const point = {lat: site.latitude, lng: site.longitude}
    let best: {id: string; miles: number} | null = null
    for (const yard of yards) {
        const places = yard.sites.length
            ? yard.sites
            : yard.latitude != null && yard.longitude != null
              ? [{latitude: yard.latitude, longitude: yard.longitude}]
              : []
        for (const place of places) {
            const miles = haversineMiles(point, {lat: place.latitude, lng: place.longitude})
            if (best == null || miles < best.miles) {
                best = {id: yard.id, miles}
            }
        }
    }
    return best?.id ?? null
}

export function hasRentalEquipment(
    assetRequirements: Pick<AssetRequirementRow, 'rentalAssetTypeId'>[],
    assetTypes: AssetTypeInfo[],
): boolean {
    return assetRequirements.some((requirement) => {
        const type = assetTypes.find((assetType) => assetType.id === requirement.rentalAssetTypeId)
        return type != null && !type.isDrivenTruck
    })
}

export function requiresPlannedEnd(
    values: {
        assetRequirements: Pick<AssetRequirementRow, 'rentalAssetTypeId'>[]
        quoteId?: string | null
    },
    assetTypes: AssetTypeInfo[],
    quote: Pick<Quote, 'rentalPeriod'> | undefined,
): boolean {
    const quoteWithoutPeriod = values.quoteId != null && quote != null && quote.rentalPeriod == null
    return !quoteWithoutPeriod && hasRentalEquipment(values.assetRequirements, assetTypes)
}

export function crewNote(
    workerRequirements: Pick<WorkerRequirementRow, 'workerTypeId'>[],
    assetRequirements: Pick<AssetRequirementRow, 'rentalAssetTypeId'>[],
    workerTypes: WorkerTypeInfo[],
): 'technicians-only' | 'no-crew' | null {
    const types = workerRequirements.flatMap(
        (row) => workerTypes.find((type) => type.id === row.workerTypeId) ?? [],
    )
    if (types.length > 0 && types.every((type) => type.isTechnician)) {
        return 'technicians-only'
    }

    return types.length === 0 && assetRequirements.length > 0 ? 'no-crew' : null
}

type RateRow = {
    billingRate?: string | null
    billingType?: string | null
    rateFromJob?: string | null
}

/** Fill empty rates from the last rate per type. Rows with a rate or a null `rateFromJob` are kept. */
export function fillRatesFromLastJobs<Row extends RateRow>(
    rows: Row[],
    typeIdOf: (row: Row) => string | null,
    requirementType: CustomerLastRate['requirementType'],
    rates: CustomerLastRate[],
): Row[] {
    return rows.map((row) => {
        const typeId = typeIdOf(row)
        if ((row.billingRate ?? '').trim() !== '' || typeId == null || row.rateFromJob === null) {
            return row
        }

        const rate = rates.find(
            (candidate) =>
                candidate.requirementType === requirementType && candidate.typeId === typeId,
        )
        // API rejects flat billing for equipment.
        if (!rate || (requirementType === 'rental_asset' && rate.billingType === 'flat')) {
            return row
        }

        return {
            ...row,
            billingRate: rate.billingRate,
            billingType: rate.billingType,
            rateFromJob: rate.jobDisplayId,
        }
    })
}

export function withoutLastJobRates<Row extends RateRow>(rows: Row[]): Row[] {
    return rows.map((row) =>
        row.rateFromJob
            ? {...row, billingRate: null, billingType: null, rateFromJob: undefined}
            : row,
    )
}

export function forPreviousCustomer(values: CreateJobFormValues): Partial<CreateJobFormValues> {
    const copied = values.cloneJobId != null
    return {
        customerContactId: null,
        locations: [],
        cloneJobId: null,
        workerRequirements: copied ? [] : withoutLastJobRates(values.workerRequirements),
        assetRequirements: copied ? [] : withoutLastJobRates(values.assetRequirements),
        ...(copied
            ? {
                  jobName: '',
                  notes: '',
                  serviceTypeId: null,
                  specificType: '',
                  categoryPickedByRep: false,
                  scheduling: {
                      ...values.scheduling,
                      serviceTypeId: null,
                      startTime: null,
                      serviceNotes: '',
                  },
              }
            : {}),
    }
}

export function isBlankJobEntry(values: CreateJobFormValues): boolean {
    const initial = createInitialFormValues()
    return (
        values.customerId == null &&
        values.quoteId == null &&
        values.cloneJobId == null &&
        !values.jobName.trim() &&
        !values.notes.trim() &&
        !values.poNumber.trim() &&
        !values.specificType?.trim() &&
        values.locations.length === 0 &&
        values.workerRequirements.length === 0 &&
        values.assetRequirements.length === 0 &&
        (values.extraCharges ?? []).length === 0 &&
        values.plannedEndDate == null &&
        !values.categoryPickedByRep &&
        values.billingMethod === initial.billingMethod &&
        JSON.stringify(values.billingTerms) === JSON.stringify(initial.billingTerms)
    )
}

type Lines = Pick<CreateJobFormValues, 'workerRequirements' | 'assetRequirements'>

function isRentalEquipmentType(
    typeId: string | null,
    assetTypes: Pick<AssetTypeInfo, 'id' | 'isDrivenTruck'>[],
): boolean {
    return assetTypes.find((type) => type.id === typeId)?.isDrivenTruck !== true
}

/** `cleared` is true when a non-empty rate without `rateFromJob` was dropped. */
export function linesBilled(
    method: ProjectBillingMethod,
    lines: Lines,
    assetTypes: Pick<AssetTypeInfo, 'id' | 'isDrivenTruck'>[] = [],
): {lines: Lines; cleared: boolean} {
    if (billsEachLine(method)) {
        return {lines, cleared: false}
    }

    const billed: Lines = {
        workerRequirements: lines.workerRequirements.map((row) => lineAsBilled(method, row, false)),
        assetRequirements: lines.assetRequirements.map((row) =>
            lineAsBilled(method, row, isRentalEquipmentType(row.rentalAssetTypeId, assetTypes)),
        ),
    }
    const before = [...lines.workerRequirements, ...lines.assetRequirements]
    const after = [...billed.workerRequirements, ...billed.assetRequirements]
    const cleared = before.some(
        (row, index) =>
            (row.billingRate ?? '').trim() !== '' &&
            !row.rateFromJob &&
            (after[index]?.billingRate ?? '').trim() === '',
    )
    return {lines: billed, cleared}
}

export function fillLastRates(lines: Lines, lastRates: CustomerLastRate[]): Lines {
    return {
        workerRequirements: fillRatesFromLastJobs(
            lines.workerRequirements,
            (row) => row.workerTypeId,
            'worker',
            lastRates,
        ),
        assetRequirements: fillRatesFromLastJobs(
            lines.assetRequirements,
            (row) => row.rentalAssetTypeId,
            'rental_asset',
            lastRates,
        ),
    }
}

type PageDefaults = {
    suggestedYardId: string | null
    lastRates: CustomerLastRate[]
    assetTypes: Pick<AssetTypeInfo, 'id' | 'isDrivenTruck'>[]
    orderMethod?: ProjectBillingMethod
}

export function withPageDefaults(
    values: CreateJobFormValues,
    defaults: PageDefaults,
): {values: CreateJobFormValues; ratesCleared: boolean} {
    const billingMethod = defaults.orderMethod ?? values.billingMethod
    const billed = linesBilled(
        billingMethod,
        fillLastRates(values, defaults.lastRates),
        defaults.assetTypes,
    )
    const dispatchYardId = values.yardPickedByRep
        ? (values.dispatchYardId ?? null)
        : (defaults.suggestedYardId ?? values.dispatchYardId ?? null)
    return {
        values: {...values, ...billed.lines, billingMethod, dispatchYardId},
        ratesCleared: billed.cleared,
    }
}

export function orderBillingMethod(
    terms: Pick<ProjectBillingTerms, 'id' | 'billingMethod'>[],
    termsId: string | null | undefined,
): ProjectBillingMethod | undefined {
    return (terms.find((candidate) => candidate.id === termsId) ?? terms[0])?.billingMethod
}

export type LinePick = {kind: 'worker' | 'equipment'; typeId: string}

export function addLine(
    values: Pick<CreateJobFormValues, 'workerRequirements' | 'assetRequirements'>,
    pick: LinePick,
): Pick<CreateJobFormValues, 'workerRequirements' | 'assetRequirements'> {
    if (pick.kind === 'worker') {
        const existing = values.workerRequirements.findIndex(
            (row) => row.workerTypeId === pick.typeId,
        )
        return {
            ...values,
            workerRequirements:
                existing >= 0
                    ? values.workerRequirements.map((row, index) =>
                          index === existing ? {...row, quantity: row.quantity + 1} : row,
                      )
                    : [
                          ...values.workerRequirements,
                          {...createEmptyWorkerRequirementRow(), workerTypeId: pick.typeId},
                      ],
        }
    }
    const existing = values.assetRequirements.findIndex(
        (row) => row.rentalAssetTypeId === pick.typeId,
    )
    return {
        ...values,
        assetRequirements:
            existing >= 0
                ? values.assetRequirements.map((row, index) =>
                      index === existing ? {...row, quantity: row.quantity + 1} : row,
                  )
                : [
                      ...values.assetRequirements,
                      {...createEmptyAssetRequirementRow(), rentalAssetTypeId: pick.typeId},
                  ],
    }
}

function getJobStartAndEndDays(values: CreateJobFormValues): {start: Date; end: Date} {
    const {startDate, endDate, severalDays} = values.scheduling
    return {start: startDate, end: severalDays ? endDate : startDate}
}

/**
 * Return what of the job's start is before now in business time: its day, or only its time on
 * today's date. A job with no time counts once its day has passed.
 */
export function pastStart(
    scheduling: Pick<CreateJobFormValues['scheduling'], 'startDate' | 'startTime'>,
    now: Date,
): 'date' | 'time' | null {
    const today = toBusinessApiDateString(now)
    const day = toApiDateString(scheduling.startDate)
    if (day !== today) {
        return day < today ? 'date' : null
    }

    const time = parseApiTime(scheduling.startTime ?? null)
    return time != null && time < formatBusinessTimestamp(now, 'HH:mm') ? 'time' : null
}

type JobEntryContext = {
    needsPlannedEnd: boolean
    activeMinimumHourlyRate?: number | null
    addToOrder?: boolean
    /** The order's method when adding to an order; unknown while its terms load. */
    orderMethod?: ProjectBillingMethod
}

export const LINE_RATE_REQUIRED = "Enter a rate, or $0 if it isn't charged"

/** Every crew and equipment line needs a rate when the order bills each line. */
function validateLineRates(values: CreateJobFormValues): Record<string, string> {
    const errors: Record<string, string> = {}
    const unpriced = (row: {billingRate?: string | null}) => (row.billingRate ?? '').trim() === ''
    values.workerRequirements.forEach((row, index) => {
        if (row.workerTypeId && unpriced(row)) {
            errors[`workerRequirements.${index}.billingRate`] = LINE_RATE_REQUIRED
        }
    })
    values.assetRequirements.forEach((row, index) => {
        if (row.rentalAssetTypeId && unpriced(row)) {
            errors[`assetRequirements.${index}.billingRate`] = LINE_RATE_REQUIRED
        }
    })
    return errors
}

export function validateJobEntry(
    values: CreateJobFormValues,
    context: JobEntryContext,
): Record<string, string> {
    const errors: Record<string, string> = {}
    const unplaced = values.locations.find(
        (location) => location.latitude == null || location.longitude == null,
    )
    const misplaced = values.locations.find(
        (location) => !isRealSite(location.latitude, location.longitude),
    )
    if (!values.customerId) {
        errors['customerId'] = 'Customer is required'
    } else if (values.locations.length === 0) {
        errors['locations'] = 'Site is required'
    } else if (unplaced) {
        errors['locations'] =
            `${unplaced.name || 'Site'} has no coordinates. Remove it and pick the site again`
    } else if (misplaced) {
        errors['locations'] =
            `${misplaced.name || 'A site'} coordinates are invalid. Check for a missing decimal point or minus sign`
    }
    if (!values.dispatchYardId) {
        errors['dispatchYardId'] = 'Dispatch yard is required'
    }
    if (!values.scheduling.serviceTypeId) {
        errors['serviceTypeId'] = 'Category is required'
    }
    if (!isRealJobName(values.jobName)) {
        errors['jobName'] = JOB_NAME_HINT
    }
    const {start, end} = getJobStartAndEndDays(values)
    if (values.scheduling.severalDays && !(end > start)) {
        errors['scheduling.endDate'] = 'End date must be after start date'
    }
    if (context.needsPlannedEnd && !values.plannedEndDate) {
        errors['plannedEndDate'] = 'Planned end date is required for rentals'
    } else if (values.plannedEndDate && values.plannedEndDate < start) {
        errors['plannedEndDate'] = 'Planned end date must be on or after start date'
    }
    Object.assign(errors, validateResources(values))
    const method = context.addToOrder ? context.orderMethod : values.billingMethod
    if (method === 'line_rates') {
        Object.assign(errors, validateLineRates(values))
    }
    if (context.addToOrder) {
        return errors
    }
    if (!values.internalContactId) {
        errors['internalContactId'] = 'Sales rep is required'
    }
    Object.assign(
        errors,
        validateBillingTerms(
            toWizardFormValues({
                billingMethod: values.billingMethod,
                billingTerms: values.billingTerms,
            }),
            context.activeMinimumHourlyRate,
        ),
    )
    return errors
}

export function sectionOf(errorKey: string): JobEntrySectionId {
    if (/^(customerId|locations|dispatchYardId)/.test(errorKey)) {
        return 'customer'
    }
    if (/^(workerRequirements|assetRequirements)/.test(errorKey)) {
        return 'crew'
    }
    if (/^(internalContactId|billingTerms|billingMethod)/.test(errorKey)) {
        return 'billing'
    }

    return 'what'
}

export function toNewOrderInput(values: CreateJobFormValues): CreateProjectInput {
    const lineItems = (values.extraCharges ?? []).flatMap(
        ({name, description, quantity, unit, unitPrice}) =>
            name.trim() && quantity != null && unitPrice != null
                ? [
                      {
                          chargeType: 'manual' as const,
                          name: name.trim(),
                          description,
                          quantity,
                          unit,
                          unitPrice,
                          totalPrice: quantity * unitPrice,
                      },
                  ]
                : [],
    )
    return {
        ...newWorkProjectInput(values),
        ...(values.quoteId ? {quoteId: values.quoteId} : {}),
        ...(lineItems.length ? {lineItems} : {}),
        jobs: [toJobInput(values)],
    }
}

/** Build the job input, clearing line rates the billing method does not use. */
function toJobInput(values: CreateJobFormValues): CreateProjectJobInput {
    const {start, end} = getJobStartAndEndDays(values)
    const {lines} = linesBilled(values.billingMethod, values)
    const serviceTypeId = values.scheduling.serviceTypeId
    if (!serviceTypeId) {
        throw new Error('Category is required')
    }
    return {
        status: values.scheduling.markJobsReadyForDispatch === false ? 'planned' : 'active',
        name: values.jobName.trim(),
        serviceTypeId,
        specificType: values.specificType?.trim() || null,
        notes: values.notes.trim() || null,
        customerContactId: values.customerContactId,
        startDate: toApiDateString(start),
        startTime: values.scheduling.startTime ?? null,
        endDate: toApiDateString(end),
        endTime: null,
        ...(values.scheduling.severalDays ? {bookByDay: true} : {}),
        plannedEndDate: values.plannedEndDate ? toApiDateString(values.plannedEndDate) : null,
        locations: jobLocationsToInputs(values.locations),
        workerRequirements: lines.workerRequirements.flatMap((row) =>
            row.workerTypeId
                ? [
                      {
                          workerTypeId: row.workerTypeId,
                          quantity: row.quantity,
                          billingRate: row.billingRate?.trim() || null,
                          billingType: row.billingType ?? null,
                          estimatedHours: row.estimatedHours ?? null,
                      },
                  ]
                : [],
        ),
        assetRequirements: lines.assetRequirements.flatMap((row) =>
            row.rentalAssetTypeId
                ? [
                      {
                          rentalAssetTypeId: row.rentalAssetTypeId,
                          quantity: row.quantity,
                          billingRate: row.billingRate?.trim() || null,
                          billingType: row.billingType ?? null,
                      },
                  ]
                : [],
        ),
    }
}

export function toAddedJobInput(values: CreateJobFormValues): CreateProjectJobInput {
    return {
        ...toJobInput(values),
        poNumber: values.poNumber.trim() || null,
        ...(values.projectBillingTermsId
            ? {projectBillingTermsId: values.projectBillingTermsId}
            : {}),
    }
}

export function nextJobDay(job: Pick<JobDetail, 'startDate' | 'endDate'>, today: Date): Date {
    const next = addDays(parseApiDate(job.endDate ?? job.startDate), 1)
    const day = startOfDay(today)
    return isBefore(next, day) ? day : next
}

export function prefillAddToOrder(
    job: JobDetail,
    base: CreateJobFormValues,
    options: {today: Date; dispatchYardId: string | null},
): CreateJobFormValues {
    const day = nextJobDay(job, options.today)
    const prefill = prefillFromJob(job, base.scheduling)
    const plannedEnd = job.plannedEndDate ? parseApiDate(job.plannedEndDate) : null
    return {
        ...base,
        ...prefill,
        workMode: 'existing',
        projectId: job.projectId,
        customerId: job.customerId,
        customerContactId:
            job.customerContactSource === 'job' ? (job.customerContact?.id ?? null) : null,
        poNumber: job.poNumber ?? '',
        projectBillingTermsId: job.projectBillingTermsId,
        dispatchYardId: options.dispatchYardId ?? base.dispatchYardId ?? null,
        yardPickedByRep: options.dispatchYardId != null,
        plannedEndDate: plannedEnd && !isBefore(plannedEnd, day) ? plannedEnd : null,
        cloneJobId: null,
        scheduling: {
            ...base.scheduling,
            ...prefill.scheduling,
            pattern: 'one_day',
            severalDays: false,
            startDate: day,
            endDate: day,
            serviceNotes: job.notes ?? '',
        },
    }
}

export function jobLength(job: Pick<JobDetail, 'startDate' | 'endDate'>): number {
    return (
        differenceInCalendarDays(
            parseApiDate(job.endDate ?? job.startDate),
            parseApiDate(job.startDate),
        ) + 1
    )
}

function locationFromJob(location: JobDetail['locations'][number]): JobLocation {
    return {
        name: location.name,
        notes: location.notes ?? '',
        positionMode: 'latlng',
        latitude: location.latitude,
        longitude: location.longitude,
        addressText: null,
        fromSavedLocationId: location.savedLocationId ?? null,
        rememberAsSavedLocation: false,
        savedLocationMode: location.savedLocationId ? 'select-saved' : 'create-new',
    }
}

export function prefillFromJob(
    job: JobDetail,
    scheduling: CreateJobFormValues['scheduling'],
): Partial<CreateJobFormValues> {
    return {
        jobName: job.name,
        serviceTypeId: job.serviceTypeId,
        specificType: job.specificType ?? '',
        notes: job.notes ?? '',
        categoryPickedByRep: true,
        workerRequirements: job.workerRequirements.map((requirement) => ({
            workerTypeId: requirement.workerTypeId,
            quantity: requirement.quantity,
            estimatedHours: requirement.estimatedHours ?? null,
            billingRate: requirement.billingRate ?? null,
            billingType: requirement.billingType ?? null,
        })),
        assetRequirements: job.assetRequirements.map((requirement) => ({
            rentalAssetTypeId: requirement.rentalAssetTypeId,
            quantity: requirement.quantity,
            billingRate: requirement.billingRate ?? null,
            billingType: requirement.billingType ?? null,
        })),
        locations: job.locations.map(locationFromJob),
        scheduling: {
            ...scheduling,
            serviceTypeId: job.serviceTypeId,
            startTime: job.startTime,
        },
    }
}

const BILLING_TYPE_BY_UNIT: Partial<Record<QuoteUnit, RequirementBillingType>> = {
    hr: 'hourly',
    day: 'daily',
    wk: 'weekly',
    mo: 'monthly',
}

const BILLING_TERM_CHARGE_TYPES = new Set(['transportation', 'disposal', 'washout', 'hsei'])

type QuoteTypeCatalog = {
    workerTypes: WorkerTypeInfo[]
    assetTypes: CategoryAssetType[]
    categories: CategoryOption[]
}

const normalizeWordsForMatching = (text: string) =>
    ` ${text
        .toLowerCase()
        .replace(/['’"]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()} `

/** The crew or equipment type whose name, as whole words, is the longest found in the description. */
export function quoteLineType(
    description: string,
    catalog: Pick<QuoteTypeCatalog, 'workerTypes' | 'assetTypes'>,
): {kind: 'worker' | 'equipment'; typeId: string; name: string} | null {
    const text = normalizeWordsForMatching(description)
    let best: {kind: 'worker' | 'equipment'; typeId: string; name: string; length: number} | null =
        null
    const candidates = [
        ...catalog.assetTypes.map((type) => ({kind: 'equipment' as const, type})),
        ...catalog.workerTypes.map((type) => ({kind: 'worker' as const, type})),
    ]
    for (const {kind, type} of candidates) {
        const name = normalizeWordsForMatching(type.name).trim()
        if (name.length < 3) {
            continue
        }
        const found = text.includes(` ${name} `) || text.includes(` ${name}s `)
        if (found && (best == null || name.length > best.length)) {
            best = {kind, typeId: type.id, name: type.name, length: name.length}
        }
    }
    return best ? {kind: best.kind, typeId: best.typeId, name: best.name} : null
}

function buildQuotedRateFields(line: QuoteItemLine | undefined, kind: 'worker' | 'equipment') {
    if (!line || line.unit === 'load') {
        return {billingRate: null, billingType: null}
    }

    return {
        billingRate: line.rate.toFixed(2),
        billingType: BILLING_TYPE_BY_UNIT[line.unit] ?? defaultBillingType(kind),
    }
}

function quoteLineHeadcount(line: QuoteItemLine): number {
    return line.unit === 'ea' || line.forRentalPeriod || line.duration != null
        ? Math.max(1, Math.round(line.quantity))
        : 1
}

/** Convert a quote line to a manual charge. Percent lines use their extended amount. */
function quoteLineCharge(
    line: QuoteItemLine | QuotePercentLine,
    lines: (QuoteItemLine | QuotePercentLine)[],
    period: QuoteRentalPeriod | null | undefined,
): ManualBillableLineItemInput {
    if (line.kind === 'percent') {
        const amount = lineExtended(line, itemSubtotal(lines, period), period)
        return {
            name: `${line.description} (${line.rate}% of quoted items)`,
            description: '',
            quantity: 1,
            unit: 'ea',
            unitPrice: amount,
        }
    }
    return {
        name: line.description,
        description: '',
        quantity: line.quantity * lineMultiplier(line, period),
        unit: line.unit,
        unitPrice: line.rate,
    }
}

/** Merge quote lines of the same type into one row at the first line's rate. */
function mergeQuoteRowsByType<Row extends {quantity: number}>(
    rows: {row: Row; typeId: string; lineName: string | null}[],
): {rows: Row[]; merged: string[]} {
    const kept = new Map<string, {row: Row; names: string[]}>()
    for (const {row, typeId, lineName} of rows) {
        const same = kept.get(typeId)
        if (same) {
            same.row = {...same.row, quantity: Math.max(same.row.quantity, row.quantity)}
            same.names.push(lineName ?? '')
        } else {
            kept.set(typeId, {row, names: [lineName ?? '']})
        }
    }
    const merged = [...kept.values()]
        .filter(({names}) => names.length > 1)
        .map(({names}) => names.filter(Boolean).join(', '))
    return {rows: [...kept.values()].map(({row}) => row), merged}
}

/** Map quote lines naming a crew or equipment type to job lines, and the rest to order charges. */
export function prefillFromQuote(
    quote: Quote,
    base: CreateJobFormValues,
    catalog: QuoteTypeCatalog = {workerTypes: [], assetTypes: [], categories: []},
): Partial<CreateJobFormValues> {
    const draft = quoteToDraft(quote)
    const period = draft.meta.rentalPeriod
    const itemLines = draft.lines.filter((line): line is QuoteItemLine => line.kind === 'item')
    const lineById = new Map(itemLines.map((line) => [line.id, line]))
    const linkedLineIds = new Set(draft.requirements.map((requirement) => requirement.lineId))
    const isBillingTerm = (line: QuoteItemLine | QuotePercentLine) =>
        line.chargeType != null && BILLING_TERM_CHARGE_TYPES.has(line.chargeType)
    const unlinked = itemLines.filter((line) => !linkedLineIds.has(line.id) && !isBillingTerm(line))
    const named = unlinked.flatMap((line) => {
        const type = quoteLineType(line.description, catalog)
        return type ? [{line, type}] : []
    })
    const namedLineIds = new Set(named.map(({line}) => line.id))
    const extraCharges: ManualBillableLineItemInput[] = draft.lines
        .filter((line) =>
            line.kind === 'item'
                ? !linkedLineIds.has(line.id) && !isBillingTerm(line) && !namedLineIds.has(line.id)
                : !isBillingTerm(line),
        )
        .map((line) => quoteLineCharge(line, draft.lines, period))
    const crew = mergeQuoteRowsByType<WorkerRequirementRow>([
        ...draft.requirements.flatMap((requirement) =>
            requirement.kind === 'worker' && requirement.workerTypeId
                ? [
                      {
                          typeId: requirement.workerTypeId,
                          lineName: lineById.get(requirement.lineId)?.description ?? null,
                          row: {
                              ...createEmptyWorkerRequirementRow(),
                              workerTypeId: requirement.workerTypeId,
                              quantity: requirement.qty,
                              ...buildQuotedRateFields(lineById.get(requirement.lineId), 'worker'),
                          },
                      },
                  ]
                : [],
        ),
        ...named.flatMap(({line, type}) =>
            type.kind === 'worker'
                ? [
                      {
                          typeId: type.typeId,
                          lineName: line.description,
                          row: {
                              ...createEmptyWorkerRequirementRow(),
                              workerTypeId: type.typeId,
                              quantity: quoteLineHeadcount(line),
                              estimatedHours:
                                  line.unit === 'hr' && !line.forRentalPeriod
                                      ? (line.duration ?? line.quantity)
                                      : null,
                              ...buildQuotedRateFields(line, 'worker'),
                          },
                      },
                  ]
                : [],
        ),
    ])
    const equipment = mergeQuoteRowsByType<AssetRequirementRow>([
        ...draft.requirements.flatMap((requirement) =>
            requirement.kind === 'equipment' && requirement.rentalAssetTypeId
                ? [
                      {
                          typeId: requirement.rentalAssetTypeId,
                          lineName: lineById.get(requirement.lineId)?.description ?? null,
                          row: {
                              ...createEmptyAssetRequirementRow(),
                              rentalAssetTypeId: requirement.rentalAssetTypeId,
                              quantity: requirement.qty,
                              ...buildQuotedRateFields(
                                  lineById.get(requirement.lineId),
                                  'equipment',
                              ),
                          },
                      },
                  ]
                : [],
        ),
        ...named.flatMap(({line, type}) =>
            type.kind === 'equipment'
                ? [
                      {
                          typeId: type.typeId,
                          lineName: line.description,
                          row: {
                              ...createEmptyAssetRequirementRow(),
                              rentalAssetTypeId: type.typeId,
                              quantity: quoteLineHeadcount(line),
                              ...buildQuotedRateFields(line, 'equipment'),
                          },
                      },
                  ]
                : [],
        ),
    ])
    const workerRequirements = crew.rows
    const assetRequirements = equipment.rows
    const category = suggestCategory(
        {
            assetTypeIds: assetRequirements.map((row) => row.rentalAssetTypeId),
            workerTypeIds: workerRequirements.map((row) => row.workerTypeId),
        },
        catalog,
    )
    const startDate = period?.kind === 'dates' ? period.startDate : base.scheduling.startDate
    const plannedEndDate =
        period?.kind === 'dates'
            ? period.endDate
            : period?.kind === 'months'
              ? addDays(startDate, rentalPeriodDays(period) - 1)
              : null
    const quotedMethod = quoteBillingMethod(draft.lines)
    const loadLine = [
        ...draft.requirements.flatMap((requirement) => lineById.get(requirement.lineId) ?? []),
        ...named.map(({line}) => line),
    ].find((line) => line.unit === 'load')
    const perLoadFromLine = quotedMethod === 'line_rates' && loadLine != null
    const mergedNote = [...crew.merged, ...equipment.merged]
        .map((names) => `${names} merged into one line at the first line rate`)
        .join('\n')
    return {
        quoteId: quote.id,
        customerId: quote.customerId,
        // A quote belongs to whoever created it, so they are the order's rep
        internalContactId: quote.createdById,
        customerContactId: quote.customerContact?.id ?? null,
        poNumber: quote.poNumber ?? '',
        jobName: quote.name?.trim() ?? '',
        workerRequirements,
        assetRequirements,
        ...(category
            ? {serviceTypeId: category.categoryId, specificType: category.specificType ?? ''}
            : {}),
        ...(mergedNote ? {notes: mergedNote} : {}),
        billingMethod: perLoadFromLine ? 'per_load' : quotedMethod,
        billingTerms: {
            ...base.billingTerms,
            ...quoteLinesToBillingTerms(draft.lines),
            ...(perLoadFromLine ? {ratePerLoad: loadLine.rate} : {}),
        },
        extraCharges,
        plannedEndDate,
        scheduling: {
            ...base.scheduling,
            startDate,
            endDate: startDate,
            ...(mergedNote ? {serviceNotes: mergedNote} : {}),
            ...(category ? {serviceTypeId: category.categoryId} : {}),
        },
    }
}
